import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axiosInstance from "../../utils/api";
import {
  FiClipboard,
  FiBriefcase,
  FiMapPin,
  FiCheck,
  FiSend,
  FiAlertCircle,
  FiLoader,
  FiArrowRight,
  FiLock,
} from "react-icons/fi";

const fetchEventInfo = async (eventId) => {
  const res = await axiosInstance.get(`/public/retro/${eventId}/info`);
  return res.data;
};

const fetchRetroData = async (eventId, code) => {
  const res = await axiosInstance.get(`/public/retro/${eventId}/${code}`);
  return res.data;
};

// Schedule submission (this is the one that locks the schedule).
const submitRetroData = async (eventId, code, activities) => {
  const res = await axiosInstance.post(`/public/retro/${eventId}/${code}`, { activities });
  return res.data;
};

const extractErrorMessage = (err, fallback) => err?.response?.data?.message || fallback;

const formatDay = (isoDate) => {
  const d = new Date(isoDate + "T00:00:00");
  return {
    weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
    date: d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
    isWeekend: [0, 6].includes(d.getDay()),
  };
};

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
      <FiAlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function OnceBanner() {
  return (
    <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
      <FiAlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        You can fill in the production schedule <strong>only one time</strong>. Once submitted it can't be changed, so make sure it's complete before submitting.
      </span>
    </div>
  );
}

export default function RetroPlanPublic() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();

  const [stage, setStage] = useState("enter"); // enter | loading | form | done
  const [code, setCode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [eventName, setEventName] = useState("");
  const [data, setData] = useState(null);
  const [checks, setChecks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [eventHasRetroPlan, setEventHasRetroPlan] = useState(true);

  const hasRetroPlan = Boolean(data?.event?.hasRetroPlan ?? eventHasRetroPlan);
  const scheduleLocked = Boolean(data?.stand?.alreadySubmitted);

  useEffect(() => {
    fetchEventInfo(eventId)
      .then((res) => {
        setEventName(res.event?.name || "");
        setEventHasRetroPlan(Boolean(res.event?.hasRetroPlan));
      })
      .catch(() => { });
  }, [eventId]);

  const runLookup = async (rawCode) => {
    const trimmed = rawCode.trim();
    if (!trimmed) {
      setErrorMsg("Enter your access code first");
      return;
    }
    setStage("loading");
    setErrorMsg("");
    try {
      const result = await fetchRetroData(eventId, trimmed);
      const initialChecks = {};
      (result.activities || []).forEach((a) => {
        initialChecks[a.name] = new Set(a.checkedDates || []);
      });
      setData(result);
      setChecks(initialChecks);
      setStage("form");
    } catch (err) {
      setErrorMsg(extractErrorMessage(err, "That code didn't work"));
      setStage("enter");
    }
  };

  useEffect(() => {
    const prefilled = searchParams.get("code");
    if (prefilled) {
      setCode(prefilled.toUpperCase());
      runLookup(prefilled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDay = (activityName, day) => {
    if (scheduleLocked) return;
    setChecks((prev) => {
      const next = { ...prev };
      const set = new Set(next[activityName]);
      if (set.has(day)) set.delete(day);
      else set.add(day);
      next[activityName] = set;
      return next;
    });
  };

  const markedCount = Object.values(checks).filter((s) => s.size > 0).length;
  const totalActivities = data?.activities?.length || 0;
  const progressPct = totalActivities > 0 ? Math.round((markedCount / totalActivities) * 100) : 0;

  const handleSubmitSchedule = async () => {
    if (scheduleLocked) return;

    const payload = Object.entries(checks)
      .filter(([, set]) => set.size > 0)
      .map(([name, set]) => ({ name, checkedDates: Array.from(set) }));

    if (payload.length === 0) {
      setErrorMsg("Check at least one day for at least one activity");
      return;
    }

    setErrorMsg("");
    setSubmitting(true);
    try {
      await submitRetroData(eventId, code.trim(), payload);
      setStage("done");
    } catch (err) {
      setErrorMsg(extractErrorMessage(err, "Couldn't submit — try again"));
    } finally {
      setSubmitting(false);
    }
  };

  const rangeLabel = (set) => {
    if (set.size === 0) return null;
    const sorted = Array.from(set).sort();
    const start = formatDay(sorted[0]);
    const end = formatDay(sorted[sorted.length - 1]);
    return sorted.length === 1 ? start.date : `${start.date} \u2192 ${end.date}`;
  };

  return (
    <div className="h-screen flex flex-col bg-stone-100 overflow-hidden">
      {/* Header */}
      <div className="shrink-0 bg-blue-950 text-white px-4 sm:px-6 py-3 flex items-center gap-3 shadow-sm z-30">
        <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
          <FiClipboard className="w-4.5 h-4.5 text-amber-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wider text-blue-300 leading-none mb-1">
            Stand production schedule
          </p>
          <p className="font-semibold truncate leading-tight">
            {data?.event?.name || eventName || "Enter your access code"}
          </p>
        </div>
      </div>

      {/* Code entry / loading */}
      {(stage === "enter" || stage === "loading") && (
        <div className="flex-1 overflow-y-auto flex items-center justify-center px-4 py-10 bg-gradient-to-b from-blue-950/5 to-stone-100">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              runLookup(code);
            }}
            className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-950 flex items-center justify-center mb-4">
              <FiClipboard className="w-5 h-5 text-amber-400" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 mb-1">Access your schedule</h1>
            <p className="text-sm text-gray-500 mb-2">
              Enter the access code you received from the stand organizer.
            </p>
            <p className="text-xs text-gray-400 mb-5">
              You can submit your production schedule only once — make sure it's complete before submitting.
            </p>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. K7P3M9"
              disabled={stage === "loading"}
              autoFocus
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-center text-xl font-semibold tracking-[0.3em] uppercase focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent mb-3 transition"
            />
            <div className="mb-3">
              <ErrorBanner message={errorMsg} />
            </div>
            <button
              type="submit"
              disabled={stage === "loading"}
              className="w-full bg-blue-950 hover:bg-blue-900 active:scale-[0.98] disabled:opacity-60 text-white font-medium rounded-xl py-3 flex items-center justify-center gap-2 transition"
            >
              {stage === "loading" ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" /> Checking code...
                </>
              ) : (
                <>
                  Continue <FiArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Event without a retro plan: nothing to fill in */}
      {stage === "form" && data && !hasRetroPlan && (
        <div className="flex-1 overflow-y-auto flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-sm text-center bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="w-14 h-14 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-4">
              <FiClipboard className="w-6 h-6" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 mb-1">No schedule needed</h1>
            <p className="text-sm text-gray-500">
              This event doesn't require a production schedule. Contact the event team if you think this is a mistake.
            </p>
          </div>
        </div>
      )}

      {/* Production schedule */}
      {stage === "form" && data && hasRetroPlan && (
        <div className="flex-1 min-h-0 flex flex-col p-3 sm:p-5 gap-3">
          <div className="shrink-0 bg-white rounded-2xl border border-gray-200 shadow-sm px-4 sm:px-5 py-3 sm:py-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-gray-900 font-semibold min-w-0">
                <FiBriefcase className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="truncate">{data.stand.exhibitorName || "Unnamed company"}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  <FiMapPin className="w-3 h-3" /> Hall {data.stand.hall || "-"}
                </span>
                <span className="inline-flex items-center bg-slate-100 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  Stand {data.stand.standNumber}
                </span>
                {data.stand.tentNumber && (
                  <span className="inline-flex items-center bg-slate-100 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    Tent {data.stand.tentNumber}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="text-xs font-medium text-gray-500 shrink-0">
                {markedCount}/{totalActivities} activities
              </span>
            </div>
          </div>

          {!scheduleLocked && (
            <div className="shrink-0">
              <OnceBanner />
            </div>
          )}

          {scheduleLocked && (
            <div className="shrink-0 flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
              <FiLock className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Your production schedule has already been submitted and can't be changed. Contact the event team if you need to make changes.
              </span>
            </div>
          )}

          <p className="shrink-0 text-sm text-gray-500 flex items-center gap-1 sm:hidden">
            Swipe the table to see all build-up days <FiArrowRight className="w-3.5 h-3.5" />
          </p>

          <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto">
              <table className="border-separate border-spacing-0 w-full">
                <thead>
                  <tr>
                    <th
                      rowSpan={2}
                      className="sticky top-0 left-0 z-30 bg-blue-950 text-left text-xs font-semibold uppercase tracking-wide text-blue-100 px-3 py-2 min-w-[170px] border-b border-r border-blue-900"
                    >
                      Activity
                    </th>
                    <th
                      colSpan={data.days.length}
                      className="sticky top-0 z-20 bg-blue-950 text-center text-[11px] font-semibold uppercase tracking-widest text-blue-200 py-1.5 border-b border-blue-900"
                    >
                      Build-Up Days
                    </th>
                  </tr>
                  <tr>
                    {data.days.map((day) => {
                      const { weekday, date, isWeekend } = formatDay(day);
                      return (
                        <th
                          key={day}
                          className={`sticky top-7 z-20 text-center text-xs font-medium py-2 px-1 min-w-[56px] border-b border-gray-200 ${isWeekend ? "bg-slate-100 text-slate-500" : "bg-white text-gray-500"
                            }`}
                        >
                          <div className="uppercase text-[10px] tracking-wide opacity-70">{weekday}</div>
                          <div className="text-gray-800 font-semibold">{date}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {(data.activities || []).map((activity, idx) => {
                    const set = checks[activity.name] || new Set();
                    const label = rangeLabel(set);
                    const rowBg = idx % 2 === 0 ? "bg-white" : "bg-slate-50/70";
                    return (
                      <tr key={activity.name}>
                        <td
                          className={`sticky left-0 z-10 ${rowBg} pr-3 pl-3 py-2 border-b border-r border-gray-200`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-blue-950 text-white text-[11px] font-semibold flex items-center justify-center shrink-0">
                              {idx + 1}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm text-gray-800 truncate">{activity.name}</p>
                              {label && (
                                <p className="text-[11px] text-amber-700 font-medium leading-tight">
                                  {label}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        {data.days.map((day) => {
                          const checked = set.has(day);
                          const { isWeekend } = formatDay(day);
                          return (
                            <td
                              key={day}
                              className={`text-center py-1.5 px-1 border-b border-gray-200 ${rowBg} ${isWeekend && !checked ? "bg-slate-50" : ""
                                }`}
                            >
                              <button
                                type="button"
                                onClick={() => toggleDay(activity.name, day)}
                                disabled={scheduleLocked}
                                aria-label={`${activity.name} on ${day}`}
                                className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg border flex items-center justify-center transition active:scale-90 disabled:cursor-not-allowed ${checked
                                  ? "bg-amber-400 border-amber-500 shadow-sm"
                                  : "bg-white border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                                  }`}
                              >
                                {checked && <FiCheck className="w-4 h-4 text-blue-950" strokeWidth={3} />}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {!scheduleLocked && (
            <div className="shrink-0 bg-white rounded-2xl border border-gray-200 shadow-sm px-4 sm:px-5 py-3">
              <div className="mb-2">
                <ErrorBanner message={errorMsg} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-500">
                  {markedCount} of {totalActivities} activities marked
                </span>
                <button
                  onClick={handleSubmitSchedule}
                  disabled={submitting}
                  className="bg-blue-950 hover:bg-blue-900 active:scale-[0.98] disabled:opacity-60 text-white font-medium rounded-xl px-5 py-2.5 flex items-center gap-2 transition"
                >
                  {submitting ? (
                    <>
                      <FiLoader className="w-4 h-4 animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4" /> Submit
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Done */}
      {stage === "done" && data && (
        <div className="flex-1 overflow-y-auto flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-sm text-center bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4">
              <FiCheck className="w-7 h-7" strokeWidth={3} />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 mb-1">Submitted</h1>
            <p className="text-sm text-gray-500 mb-4">
              Thanks — the production schedule for{" "}
              <span className="font-medium text-gray-700">{data.stand.exhibitorName}</span> has been sent to the
              event team.
            </p>
            <p className="text-xs text-gray-400">
              The production schedule can't be changed after submission. Contact the event team if you need to make
              changes.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}